import { useEffect, useState, type FC } from 'react'
import { Box, Stack, Typography, Button, Tooltip } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import toast from 'react-hot-toast'
import { UploadImage, CloseIcon, LocalPinIcon } from '@/components/icons'
import uploadApi from '@/api/admin/upload'
import { useTranslation } from '@/hooks/useTranslation'
import type { GridCellCtx } from '@/components/grid/types'
import type { StationRow } from './column-defs-builder'

interface RendererOptions {
  bulkEditMode: boolean
  isDark?: boolean
  onOpenGPSPicker?: (row: StationRow) => void
  onEditItem?: (id: string) => void
  onDeleteItem?: (id: string, name?: string) => void
  /**
   * Called after the cell stages an edit itself, outside the grid's editors.
   * The page cannot see the grid's buffer change, and it is this that arms the
   * autosave (SCRUM-1032 round 7 review: a photo change was never written).
   */
  onStaged?: () => void
}

/**
 * Station photo cell: uploads to S3 and stages the resulting url/filename as a
 * pending edit, so the change travels with every other unsaved edit and a
 * refetch of the base rows cannot drop it.
 */
export const createUploadPhotoCellRenderer =
  ({ bulkEditMode, onStaged }: RendererOptions): FC<GridCellCtx<StationRow>> =>
  ({ row, rowId, api }) => {
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(false)
    const [imageUrl, setImageUrl] = useState(row?.pictureUrl || '')
    const [fileName, setFileName] = useState(row?.pictureFileName || '')
    const canEdit = bulkEditMode && Number(row?.stationType) !== 1

    useEffect(() => {
      setImageUrl(row?.pictureUrl || '')
      setFileName(row?.pictureFileName || '')
    }, [row?.pictureUrl, row?.pictureFileName])

    const handleImgUpload = async (event: any) => {
      const file = event.target.files?.[0]
      if (!file) {
        toast.error(t('stationsFileUploadInvalid'))
        return
      }
      setIsLoading(true)
      const formData = new FormData()
      formData.append('File', file)
      const uploaded = await uploadApi
        .uploadToS3(formData)
        .then((response) => response.data)
        .catch(() => {
          toast.error(t('stationsUploadFailed'))
          return null
        })
      setIsLoading(false)
      if (!uploaded) return

      setImageUrl(uploaded.s3Url)
      setFileName(uploaded.fileName)
      api.setPendingEdit(rowId, {
        pictureUrl: uploaded.s3Url,
        pictureFileName: uploaded.fileName,
        stationPhoto: file.name,
      })
      onStaged?.()
    }

    const handleRemove = (event: any) => {
      event.stopPropagation()
      event.preventDefault()
      setImageUrl('')
      setFileName('')
      api.setPendingEdit(rowId, {
        pictureUrl: '',
        pictureFileName: '',
        stationPhoto: '',
      })
      onStaged?.()
    }

    return (
      <LoadingButton
        data-no-cell-edit
        loading={isLoading}
        loadingPosition="end"
        variant="contained"
        component={imageUrl ? 'div' : 'label'}
        disabled={!canEdit}
        sx={{
          width: '130px',
          height: '36px',
          borderRadius: '4px',
          backgroundColor: 'background.overrideDefault',
          color: '#181818',
          fontSize: '12px',
          padding: '6px 10px',
          justifyContent: 'space-between',
          alignItems: 'center',
          display: 'flex',
          minHeight: 'unset',
          overflow: 'hidden',
          '&:hover': { backgroundColor: 'background.overrideDefault' },
          '&.Mui-disabled': {
            backgroundColor: 'background.overrideDefault',
            color: '#181818',
            opacity: 0.6,
          },
        }}
        endIcon={
          !imageUrl ? (
            <UploadImage sx={{ color: 'text.main', fontSize: '14px' }} />
          ) : (
            <CloseIcon
              sx={{ color: 'text.main', fontSize: '14px' }}
              onClick={handleRemove}
            />
          )
        }
      >
        {!imageUrl && canEdit && (
          <input type="file" hidden onChange={handleImgUpload} />
        )}
        <Stack
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
          width="100%"
          gap={0.5}
          sx={{ overflow: 'hidden' }}
        >
          {fileName ? (
            <Tooltip title={fileName}>
              <Typography
                fontSize="10px"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  maxWidth: imageUrl ? '70px' : '100px',
                }}
              >
                {fileName}
              </Typography>
            </Tooltip>
          ) : (
            <Typography fontSize="12px" sx={{ flex: 1, lineHeight: '20px' }}>
              {t('stationsImageUpload')}
            </Typography>
          )}
          {imageUrl && (
            <Box
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '2px',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <img
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                src={imageUrl}
                alt=""
              />
            </Box>
          )}
        </Stack>
      </LoadingButton>
    )
  }

/** Opens the map picker for the row's geo tag. */
export const createGPSCoordinatesCellRenderer =
  ({ bulkEditMode, onOpenGPSPicker }: RendererOptions): FC<GridCellCtx<StationRow>> =>
  ({ row, rowId, api }) => {
    const { t } = useTranslation()

    // Moving a station between areas moves its pin too, so the picker is held
    // back until that edit has been saved.
    const pendingEdit = api.getPendingEdits().get(rowId) as
      | Record<string, unknown>
      | undefined
    const isAreaEdited = Boolean(
      pendingEdit && ('area' in pendingEdit || 'subArea' in pendingEdit),
    )
    const canEdit = bulkEditMode && !isAreaEdited

    const coordinates =
      row?.grsCoordinatedLatitude && row?.grsCoordinatedLongitude
        ? `${row.grsCoordinatedLatitude.toFixed(6)}\n${row.grsCoordinatedLongitude.toFixed(6)}`
        : ''

    const tooltipText = isAreaEdited ? t('stationsGeotagSaveAreaFirst') : ''

    return (
      <Tooltip title={tooltipText} arrow disableHoverListener={!tooltipText}>
        <span style={{ display: 'inline-block' }} data-no-cell-edit>
          <Button
            variant="contained"
            component="label"
            onClick={() => {
              if (canEdit) onOpenGPSPicker?.(row)
            }}
            disabled={!canEdit}
            sx={{
              marginTop: '10px',
              width: '150px',
              borderRadius: '4px',
              backgroundColor: 'background.overrideDefault',
              color: '#181818',
              fontSize: '10px',
              padding: '8px 12px',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              minHeight: 'unset',
              '&:hover': { backgroundColor: 'background.overrideDefault' },
              '&.Mui-disabled': {
                backgroundColor: 'background.overrideDefault',
                color: '#181818',
                opacity: 0.6,
              },
            }}
            endIcon={<LocalPinIcon sx={{ color: 'text.main', fontSize: '14px' }} />}
          >
            <Typography
              fontSize="10px"
              sx={{ whiteSpace: 'pre-line', lineHeight: '1.2', textAlign: 'center' }}
            >
              {coordinates || t('addCoordinates')}
            </Typography>
          </Button>
        </span>
      </Tooltip>
    )
  }

/** EDIT / DELETE pair, shown while the grid is in bulk-edit mode. */
export const createBulkActionCellRenderer =
  ({
    bulkEditMode,
    isDark,
    onEditItem,
    onDeleteItem,
  }: RendererOptions): FC<GridCellCtx<StationRow>> =>
  ({ row }) => {
    const { t } = useTranslation()

    if (!bulkEditMode || !row?.id) return null
    if (Number(row.id) < 0) return null

    const id = String(row.id)
    const editColor = isDark ? '#5fe0ad' : '#004744'
    const editHover = isDark ? '#8bf0c6' : '#003533'
    const deleteColor = isDark ? '#f87171' : '#d32f2f'
    const deleteHover = isDark ? '#fca5a5' : '#c62828'

    return (
      <Stack
        direction="row"
        spacing={2}
        data-no-cell-edit
        sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            letterSpacing: '0.3px',
            color: editColor,
            cursor: onEditItem ? 'pointer' : 'not-allowed',
            userSelect: 'none',
            transition: 'color 0.2s ease',
            '&:hover': onEditItem ? { color: editHover } : {},
          }}
          onClick={() => onEditItem?.(id)}
        >
          {t('commonActionsEdit')}
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            letterSpacing: '0.3px',
            color: deleteColor,
            cursor: onDeleteItem ? 'pointer' : 'not-allowed',
            userSelect: 'none',
            transition: 'color 0.2s ease',
            '&:hover': onDeleteItem ? { color: deleteHover } : {},
          }}
          onClick={() =>
            onDeleteItem?.(
              id,
              row.controller
                ? `${row.controller} (${row.address}-${row.index})`
                : id,
            )
          }
        >
          {t('commonActionsDelete')}
        </Typography>
      </Stack>
    )
  }
